import styled from '@emotion/styled';
import {Color} from '@nfq/colors';
import {spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';

import {P} from 'UI/components/layout/Text';

import {useFileCard} from './useFileCard';
import {Close, File as FileIcon} from 'Images/icons';

import type {ValidationFunc} from 'Shared/formHelperTypes';

/**
 * The `ComponentProps` interface defines the properties for the `FileCard` component.
 * This interface specifies the structure of props required to render a file card that displays
 * file information with delete functionality. It includes the file object, its position in a list,
 * an optional deletion handler, and a test identifier for component testing purposes.
 */
interface ComponentProps {
    /**
     * The file object representing the uploaded file to be displayed in the card.
     * This File object contains metadata such as name, size, and type information
     * that will be used to render the file details within the component.
     */
    file: File;
    /**
     * The index position of this file within the upload list or file collection.
     * This numeric value is used to identify the specific file when performing
     * operations like deletion and helps maintain proper order in file listings.
     */
    index: number;
    /**
     * Optional callback function that handles file deletion from the upload list.
     * When provided, this function receives the file's index as a parameter and
     * is triggered when the user clicks the delete button on the file card.
     */
    onDelete?(index: number): void;
    /**
     * The `testId` property represents a unique identifier for testing purposes.
     * This property is crucial for uniquely identifying components during testing,
     * allowing for more accurate and reliable automated tests and component selection.
     *
     * @default 'FileCard'
     */
    testId?: string;
    /**
     * Validation function to validate the file input.
     */
    validate: ValidationFunc;
}

/**
 * The `FileCard` component renders a file card that displays information about an uploaded file.
 * This component provides a visual representation of a file with its name, size, and an optional
 * delete functionality through a close button. The card follows a consistent design pattern with
 * proper spacing, theming support, and accessibility considerations for file management interfaces.
 *
 * @param props          The component props.
 * @param props.file     The file object representing the uploaded file to be displayed in the card.
 * @param props.index    The index position of this file within the upload list or file collection.
 * @param props.onDelete Optional callback function that handles file deletion from the upload list.
 * @param props.testId   Test identifier for component testing and automation purposes.
 * @param props.validate Validation function to validate the file input.
 * @returns A React element representing the `FileCard` component with file information and controls.
 *
 * @example
 * ```tsx
 * const handleFileDelete = (index: number) => {
 *     console.log(`Deleting file at index ${index}`);
 * };
 *
 * const uploadedFile = new File(['content'], 'document.pdf', { type: 'application/pdf' });
 *
 * <FileCard
 *     file={uploadedFile}
 *     index={0}
 *     onDelete={handleFileDelete}
 *     testId="uploaded-file-card"
 * />
 * ```
 */
const FileCard = observer(({file, index, onDelete, testId = 'FileCard', validate}: ComponentProps) => {
    const {colors, error, handleDelete, name, size} = useFileCard(file, index, validate, onDelete);

    return (
        <Wrapper $error={(error?.length ?? 0) > 0} data-cy={testId}>
            <IconWrapper>
                <FileIcon color1={(error?.length ?? 0) > 0 ? colors.errorColor : undefined} />
            </IconWrapper>
            <TextWrapper>
                <StyledP>{name}</StyledP>
                <StyledP><Color $color={colors.textNeutral}>{size} von 10 MB</Color></StyledP>
                {error && (
                    <P><Color $color={colors.errorColor}>{error}</Color></P>
                )}
            </TextWrapper>
            <IconWrapperButton type="button" onClick={handleDelete}>
                <Close />
            </IconWrapperButton>
        </Wrapper>
    );
});

FileCard.displayName = 'FileCard';

export {FileCard};

interface WrapperProps {
    $error: boolean;
}

const Wrapper = styled.div<WrapperProps>`
    align-items: center;
    border: 1px solid ${({$error, theme}) => ($error ? theme.colors.errorColor : theme.colors.darkBorder)};
    border-radius: 16px;
    display: flex;
    gap: ${spacing(2)};
    max-width: 40rem;
    padding: ${spacing(4)} ${spacing(4)} ${spacing(4)} ${spacing(6)};
    width: 100%;
`;

const IconWrapper = styled.div`
    flex: 0 0 auto;
    padding-inline-end: ${spacing(4)};
`;

const TextWrapper = styled.div`
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-width: 0;
`;

const StyledP = styled(P)`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const IconWrapperButton = styled.button`
    appearance: none;
    background: none;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    flex: 0 0 auto;
    height: 2.4rem;
    outline: 1px solid hsl(from ${({theme}) => theme.colors.tertiary} H S L / 0);
    padding: 0;
    transition: outline 0.2s ease-in-out;
    width: 2.4rem;

    &:focus-visible {
        outline: 1px solid hsl(from ${({theme}) => theme.colors.tertiary} H S L / 1);
    }
`;